import { Link } from 'react-router-dom';
import { Home } from 'lucide-react';

const RefundPolicy = () => {
  return (
    <div className="min-h-screen bg-[#F5F1EA]" data-testid="refund-policy-page">
      {/* Header */}
      <div className="border-b border-[#0A0A0A]/10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-2 text-[#525252] hover:text-[#0A0A0A] transition-colors w-fit">
            <Home className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-heading font-bold text-[#0A0A0A] mb-2">Refund & Returns Policy</h1>
        <p className="text-[#525252] mb-8">Last updated: {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

        <div className="prose prose max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">1. Overview</h2>
            <p className="text-[#525252] leading-relaxed">
              At Sol Digital, we strive to provide high-quality products and services. This Refund & Returns Policy outlines the conditions under which refunds and returns are processed. This policy is in compliance with the Consumer Protection Act, 2019 and the Consumer Protection (E-Commerce) Rules, 2020 of India.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">2. Physical NFC Cards</h2>
            
            <h3 className="text-xl font-medium text-[#0A0A0A] mb-3">2.1 Eligibility for Returns</h3>
            <p className="text-[#525252] leading-relaxed mb-4">You may return your NFC card under the following conditions:</p>
            <ul className="list-disc list-inside text-[#525252] space-y-2">
              <li>The card is defective or damaged upon receipt</li>
              <li>The card does not function as described</li>
              <li>You received the wrong product</li>
              <li>Return request is made within 7 days of delivery</li>
            </ul>

            <h3 className="text-xl font-medium text-[#0A0A0A] mb-3 mt-6">2.2 Non-Returnable Items</h3>
            <p className="text-[#525252] leading-relaxed mb-4">The following items cannot be returned:</p>
            <ul className="list-disc list-inside text-[#525252] space-y-2">
              <li>Cards that have been physically damaged by the customer</li>
              <li>Cards that have been customized or personalized after purchase</li>
              <li>Cards returned after 7 days of delivery</li>
              <li>Cards without proof of purchase</li>
            </ul>

            <h3 className="text-xl font-medium text-[#0A0A0A] mb-3 mt-6">2.3 Return Process</h3>
            <ol className="list-decimal list-inside text-[#525252] space-y-2">
              <li>Contact our support team at support@soldigital.in within 7 days of delivery</li>
              <li>Provide your order ID and reason for return</li>
              <li>Include photos or videos if the product is defective</li>
              <li>Wait for return authorization from our team</li>
              <li>Ship the product back to the address provided (prepaid if defective)</li>
              <li>Refund will be processed within 7-10 business days of receiving the returned item</li>
            </ol>

            <h3 className="text-xl font-medium text-[#0A0A0A] mb-3 mt-6">2.4 Refund Amount</h3>
            <ul className="list-disc list-inside text-[#525252] space-y-2">
              <li><strong>Defective/Wrong Product:</strong> Full refund including shipping charges</li>
              <li><strong>Change of Mind:</strong> Product cost minus shipping charges (customer bears return shipping)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">3. Digital Subscription</h2>
            
            <h3 className="text-xl font-medium text-[#0A0A0A] mb-3">3.1 Subscription Refunds</h3>
            <p className="text-[#525252] leading-relaxed mb-4">
              Digital subscription fees are generally non-refundable. However, refunds may be considered in the following cases:
            </p>
            <ul className="list-disc list-inside text-[#525252] space-y-2">
              <li>Technical issues preventing access to your profile for more than 7 consecutive days</li>
              <li>Duplicate charges or billing errors</li>
              <li>Service discontinuation by Sol Digital</li>
            </ul>

            <h3 className="text-xl font-medium text-[#0A0A0A] mb-3 mt-6">3.2 Subscription Cancellation</h3>
            <p className="text-[#525252] leading-relaxed">
              You may cancel your subscription at any time. However, no prorated refunds will be provided for the remaining subscription period. Your profile will remain active until the subscription end date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">4. Refund Processing</h2>
            
            <h3 className="text-xl font-medium text-[#0A0A0A] mb-3">4.1 Refund Method</h3>
            <p className="text-[#525252] leading-relaxed mb-4">
              Refunds will be processed to the original payment method:
            </p>
            <ul className="list-disc list-inside text-[#525252] space-y-2">
              <li><strong>UPI:</strong> 2-4 business days</li>
              <li><strong>Credit/Debit Card:</strong> 5-7 business days</li>
              <li><strong>Net Banking:</strong> 5-7 business days</li>
              <li><strong>Wallets:</strong> 2-4 business days</li>
            </ul>

            <h3 className="text-xl font-medium text-[#0A0A0A] mb-3 mt-6">4.2 Refund Timeline</h3>
            <p className="text-[#525252] leading-relaxed">
              Once your refund is approved, it will be processed within 7-10 business days. The actual time for the refund to reflect in your account depends on your payment provider.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">5. Damaged or Defective Products</h2>
            <p className="text-[#525252] leading-relaxed mb-4">
              If you receive a damaged or defective NFC card:
            </p>
            <ol className="list-decimal list-inside text-[#525252] space-y-2">
              <li>Do not attempt to repair the card yourself</li>
              <li>Take photos/videos of the damage immediately upon receipt</li>
              <li>Contact us within 48 hours of delivery</li>
              <li>We will arrange for a free replacement or full refund</li>
            </ol>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">6. Order Cancellation</h2>
            
            <h3 className="text-xl font-medium text-[#0A0A0A] mb-3">6.1 Before Dispatch</h3>
            <p className="text-[#525252] leading-relaxed">
              Orders can be cancelled for a full refund if the request is made before the product is dispatched. Contact us immediately at support@soldigital.in.
            </p>

            <h3 className="text-xl font-medium text-[#0A0A0A] mb-3 mt-6">6.2 After Dispatch</h3>
            <p className="text-[#525252] leading-relaxed">
              Once the product has been dispatched, cancellation is not possible. You may request a return after receiving the product as per our return policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">7. Exchange Policy</h2>
            <p className="text-[#525252] leading-relaxed">
              We offer exchanges for defective or damaged products only. If you wish to exchange for a different product, please return the original item for a refund and place a new order.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">8. Exceptions</h2>
            <p className="text-[#525252] leading-relaxed mb-4">
              Refunds may be denied in cases of:
            </p>
            <ul className="list-disc list-inside text-[#525252] space-y-2">
              <li>Suspected fraud or abuse of the refund policy</li>
              <li>Products damaged due to misuse by the customer</li>
              <li>Requests made after the specified timeframes</li>
              <li>Violation of our Terms and Conditions</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">9. Contact Us</h2>
            <p className="text-[#525252] leading-relaxed">
              For refund or return requests, please contact us:
            </p>
            <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6 mt-4">
              <p className="text-[#0A0A0A] font-medium">Sol Digital Support</p>
              <p className="text-[#525252]">Email: support@soldigital.in</p>
              <p className="text-[#525252]">Response Time: Within 24-48 hours</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#0A0A0A] mb-4">10. Consumer Rights</h2>
            <p className="text-[#525252] leading-relaxed">
              This policy does not affect your statutory rights under the Consumer Protection Act, 2019. If you have any complaints that are not resolved to your satisfaction, you may file a complaint with the appropriate Consumer Forum.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;
